import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronUp } from "lucide-react";
import { format } from "date-fns";
import { nl } from "date-fns/locale";

export const OrdersManagement = () => {
  const [orders, setOrders] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [expandedOrder, setExpandedOrder] = useState<string | null>(null);

  useEffect(() => {
    fetchOrders();
  }, [sortOrder]);

  const fetchOrders = async () => {
    try {
      const { data, error } = await supabase
        .from("orders")
        .select("*")
        .order("created_at", { ascending: sortOrder === "asc" });

      if (error) throw error;
      setOrders(data || []);
    } catch (error: any) {
      console.error("Error fetching orders:", error);
      toast.error("Kon bestellingen niet laden");
    } finally {
      setIsLoading(false);
    }
  };

  const toggleSortOrder = () => {
    setSortOrder(sortOrder === "asc" ? "desc" : "asc");
  };

  const toggleOrderDetails = (orderId: string) => {
    setExpandedOrder(expandedOrder === orderId ? null : orderId);
  };

  if (isLoading) {
    return <div className="text-center py-12">Laden...</div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Bestellingen Overzicht</h2>
        <Button variant="outline" onClick={toggleSortOrder}>
          Sorteer op Datum
          {sortOrder === "desc" ? (
            <ChevronDown className="w-4 h-4 ml-2" />
          ) : (
            <ChevronUp className="w-4 h-4 ml-2" />
          )}
        </Button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Bestelnummer</TableHead>
              <TableHead>Klant</TableHead>
              <TableHead>Datum</TableHead>
              <TableHead>Totaal</TableHead>
              <TableHead>Status</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {orders.map((order) => (
              <>
                <TableRow
                  key={order.id}
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => toggleOrderDetails(order.id)}
                >
                  <TableCell className="font-mono text-sm">
                    #{order.id.slice(0, 8)}
                  </TableCell>
                  <TableCell>
                    <div>
                      <div className="font-medium">{order.customer_name || "Onbekend"}</div>
                      <div className="text-sm text-muted-foreground">{order.customer_email}</div>
                    </div>
                  </TableCell>
                  <TableCell>
                    {format(new Date(order.created_at), "d MMM yyyy, HH:mm", { locale: nl })}
                  </TableCell>
                  <TableCell className="font-medium">€{order.total_amount}</TableCell>
                  <TableCell>
                    <span
                      className={`px-2 py-1 rounded-full text-xs ${
                        order.payment_status === "paid"
                          ? "bg-green-100 text-green-700"
                          : "bg-yellow-100 text-yellow-700"
                      }`}
                    >
                      {order.payment_status === "paid" ? "Betaald" : "In behandeling"}
                    </span>
                  </TableCell>
                  <TableCell>
                    {expandedOrder === order.id ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )}
                  </TableCell>
                </TableRow>

                {expandedOrder === order.id && (
                  <TableRow>
                    <TableCell colSpan={6} className="bg-gray-50">
                      <div className="p-4 space-y-4">
                        <div>
                          <h4 className="font-semibold mb-2">Bestelde Producten:</h4>
                          <ul className="space-y-1">
                            {(Array.isArray(order.items) ? order.items : JSON.parse(order.items)).map((item: any, idx: number) => (
                              <li key={idx}>
                                {item.quantity}x {item.name} - €{item.price}
                              </li>
                            ))}
                          </ul>
                        </div>

                        {order.shipping_address && (
                          <div>
                            <h4 className="font-semibold mb-2">Verzendadres:</h4>
                            <div className="text-sm">
                              {typeof order.shipping_address === 'string' 
                                ? (() => {
                                    const addr = JSON.parse(order.shipping_address);
                                    return (
                                      <>
                                        {addr.street} {addr.houseNumber}<br />
                                        {addr.postalCode} {addr.city}<br />
                                        {addr.country}
                                      </>
                                    );
                                  })()
                                : (
                                  <>
                                    {order.shipping_address.street} {order.shipping_address.houseNumber}<br />
                                    {order.shipping_address.postalCode} {order.shipping_address.city}<br />
                                    {order.shipping_address.country}
                                  </>
                                )}
                            </div>
                          </div>
                        )}

                        <div>
                          <h4 className="font-semibold mb-2">Stripe Details:</h4>
                          <div className="text-sm text-muted-foreground font-mono">
                            Session: {order.stripe_session_id}
                            {order.stripe_payment_intent_id && (
                              <>
                                <br />
                                Payment Intent: {order.stripe_payment_intent_id}
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </>
            ))}
          </TableBody>
        </Table>

        {orders.length === 0 && (
          <div className="text-center py-12 text-muted-foreground">
            Nog geen bestellingen
          </div>
        )}
      </div>
    </div>
  );
};
